/**
 * Self-service change-password dialog (any logged-in admin user).
 *
 * Shows the "current password" field only when the user already has one — a
 * passwordless OIDC/barcode-only user sets a first password without it. No
 * client-side quality rules (kept simple, matching the backend).
 */

import { useState } from "react";
import { App as AntdApp, Form, Input, Modal } from "antd";

import { ApiError, api } from "../api/client";

export function ChangePasswordModal({
  open,
  hasPassword,
  onClose,
}: {
  open: boolean;
  hasPassword: boolean;
  onClose: () => void;
}) {
  const { message } = AntdApp.useApp();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    setCurrent("");
    setNext("");
    onClose();
  };

  const submit = async () => {
    if (!next) {
      message.error("Enter a new password");
      return;
    }
    setBusy(true);
    try {
      await api.changePassword({
        ...(hasPassword ? { current_password: current } : {}),
        new_password: next,
      });
      message.success("Password updated");
      close();
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Change password"
      okText="Save"
      confirmLoading={busy}
      onOk={submit}
      onCancel={close}
    >
      <Form layout="vertical">
        {hasPassword && (
          <Form.Item label="Current password">
            <Input.Password
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          </Form.Item>
        )}
        <Form.Item label="New password">
          <Input.Password
            value={next}
            onChange={(e) => setNext(e.target.value)}
            onPressEnter={submit}
            autoComplete="new-password"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
